import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { newEventId, trackLead, hasMarketingConsent, readCookie } from "../../lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { value: "ceo_fundador", label: "CEO / Fundador" },
  { value: "marketing", label: "Marketing Manager" },
  { value: "agencia", label: "Agencia de Marketing" },
  { value: "otro", label: "Otro" },
];

const OBJETIVOS = [
  { value: "generar_leads", label: "Generar leads cualificados" },
  { value: "brand_awareness", label: "Posicionar mi marca como referente" },
  { value: "contenido_rrss", label: "Crear contenido para redes sociales" },
  { value: "otro", label: "Otro" },
];

const PRESUPUESTOS = [
  { value: "menos_1500", label: "Menos de 1.500€ / mes" },
  { value: "1500_3000", label: "1.500€ – 3.000€ / mes" },
  { value: "3000_5000", label: "3.000€ – 5.000€ / mes" },
  { value: "mas_5000", label: "Más de 5.000€ / mes" },
  { value: "no_lo_se", label: "Aún no lo sé" },
];

const MODALIDADES = [
  { value: "estudio", label: "En vuestro estudio (Madrid)" },
  { value: "desplazamiento", label: "En mi ubicación (os desplazáis)" },
  { value: "indiferente", label: "Me da igual / quiero que me asesoréis" },
];

const TIMINGS = [
  { value: "inmediato", label: "Quiero empezar ya" },
  { value: "1_mes", label: "En 1-3 meses" },
  { value: "3_meses", label: "En 3-6 meses" },
  { value: "explorando", label: "Solo estoy explorando" },
];

const CLIENTES_POTENCIALES = [
  { value: "1_2", label: "1 o 2 clientes" },
  { value: "3_5", label: "Entre 3 y 5 clientes" },
  { value: "mas_5", label: "Más de 5 clientes" },
  { value: "no_lo_se", label: "Aún no lo sé, quiero explorarlo" },
];

const BENEFICIOS = [
  { value: "referente", label: "Que me conozcan y me vean como referente de mi sector" },
  { value: "confianza", label: "Generar confianza antes de la primera reunión" },
  { value: "oportunidades", label: "Conseguir más reuniones y oportunidades de venta" },
  { value: "contenido", label: "Tener contenido constante para redes sociales" },
  { value: "networking", label: "Conectar con clientes potenciales invitándoles al podcast" },
  { value: "diferenciacion", label: "Diferenciarme de mi competencia" },
  { value: "talento", label: "Atraer talento y reforzar la marca internamente" },
  { value: "no_lo_se", label: "Aún no lo sé, quiero que me asesoréis" },
];

const BENEFICIOS_AGENCIA = [
  { value: "referente", label: "Que mis clientes sean vistos como referentes de su sector" },
  { value: "confianza", label: "Que generen confianza antes de la primera reunión" },
  { value: "oportunidades", label: "Que consigan más reuniones y oportunidades de venta" },
  { value: "contenido", label: "Tener contenido constante para sus redes sociales" },
  { value: "networking", label: "Que conecten con clientes potenciales invitándoles al podcast" },
  { value: "diferenciacion", label: "Diferenciarles de su competencia" },
  { value: "fee", label: "Ampliar el fee mensual de mi agencia" },
  { value: "no_lo_se", label: "Aún no lo sé, quiero que me asesoréis" },
];

const VARIANTS = {
  home: {
    badge: "Solicitar propuesta personalizada",
    title: (
      <>
        ¿Quieres una <span className="gradient-text">propuesta a medida</span>?
      </>
    ),
    subtitle:
      "Cuéntanos sobre tu proyecto y te preparamos una propuesta personalizada en menos de 24h.",
    step1Title: "Cuéntanos quién eres",
    step1Subtitle: "Datos básicos para que podamos contactarte.",
    empresaLabel: "Empresa *",
    empresaPlaceholder: "Nombre de tu empresa",
    webLabel: "Web de tu empresa *",
    webPlaceholder: "www.tuempresa.com",
    webHint: "Nos sirve para preparar la propuesta antes de llamarte. Si todavía no tienes web, escribe \"no tengo\".",
    step2Title: "Tu rol y objetivo",
    step2Subtitle: "Nos ayuda a preparar una propuesta más ajustada a tu situación.",
    rolLabel: "¿Cuál es tu rol? *",
    objetivoLabel: "¿Cuál es tu objetivo principal? *",
    beneficiosLabel: "¿En qué crees que te puede ayudar tener un videopodcast? *",
    beneficiosHint: "Puedes marcar varias opciones.",
    beneficios: BENEFICIOS,
    step3Title: "Formato, presupuesto y timing",
    step3Subtitle: "Para saber si podemos ayudarte y con qué tipo de propuesta.",
    presupuestoLabel: "Presupuesto mensual estimado *",
    timingLabel: "¿Cuándo quieres empezar? *",
    privacyText: "y consiento el tratamiento de mis datos para recibir una propuesta comercial de Galvani Studios.",
    submitLabel: "Solicitar propuesta",
    showModalidad: true,
    showClientes: false,
    origen: "web",
    roles: ROLES,
    objetivos: OBJETIVOS,
    presupuestos: PRESUPUESTOS,
  },
  agencias: {
    badge: "Programa de partners para agencias",
    title: (
      <>
        Hablemos de tu <span className="gradient-text">acuerdo como partner</span>
      </>
    ),
    subtitle:
      "Cuéntanos cómo trabaja tu agencia y te preparamos una propuesta white label con tarifas de partner. Respuesta en menos de 24h.",
    step1Title: "Cuéntanos de tu agencia",
    step1Subtitle: "Datos básicos para que podamos contactarte.",
    empresaLabel: "Nombre de la agencia *",
    empresaPlaceholder: "Nombre de tu agencia",
    webLabel: "Web de tu agencia *",
    webPlaceholder: "www.tuagencia.com",
    webHint: "Le echamos un vistazo antes de la llamada para entender a qué tipo de clientes trabajas.",
    step2Title: "Tu rol y qué buscas",
    step2Subtitle: "Así sabemos si vienes a cerrar un proveedor o a explorar el servicio.",
    rolLabel: "¿Cuál es tu rol en la agencia? *",
    objetivoLabel: "¿Qué te trae aquí? *",
    beneficiosLabel: "¿En qué crees que le puede ayudar un videopodcast a tus clientes? *",
    beneficiosHint: "Puedes marcar varias opciones.",
    beneficios: BENEFICIOS_AGENCIA,
    step3Title: "Volumen y timing",
    step3Subtitle: "Para dimensionar el acuerdo y las tarifas de partner.",
    presupuestoLabel: "Presupuesto medio que manejan tus clientes en contenido *",
    timingLabel: "¿Cuándo te gustaría arrancar? *",
    privacyText: "y consiento el tratamiento de mis datos para recibir una propuesta de colaboración de Galvani Studios.",
    submitLabel: "Quiero más información",
    showModalidad: false,
    showClientes: true,
    origen: "agencias",
    roles: [
      { value: "ceo_fundador", label: "Fundador/a o socio/a" },
      { value: "agencia", label: "Director/a de cuentas" },
      { value: "marketing", label: "Responsable de contenidos" },
      { value: "otro", label: "Otro" },
    ],
    objetivos: [
      { value: "busco_proveedor", label: "Ya vendo videopodcast y busco proveedor" },
      { value: "ampliar_servicios", label: "Quiero añadirlo a mi catálogo de servicios" },
      { value: "subir_fee", label: "Quiero subir el fee y retener a mis clientes" },
      { value: "otro", label: "Otro" },
    ],
    presupuestos: PRESUPUESTOS,
  },
};

export default function LeadForm({ variant = "home" }) {
  const cfg = VARIANTS[variant] || VARIANTS.home;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    web: "",
    email: "",
    telefono: "",
    rol: "",
    objetivo: "",
    objetivo_otro: "",
    modalidad: "",
    clientes_potenciales: "",
    beneficios: [],
    tiene_presupuesto: "",
    timeline: "",
    accepted_privacy: false,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleBeneficio = (value) =>
    setForm((f) => ({
      ...f,
      beneficios: f.beneficios.includes(value)
        ? f.beneficios.filter((v) => v !== value)
        : [...f.beneficios, value],
    }));

  const canNext = () => {
    if (step === 1) return form.nombre.trim() && form.empresa.trim() && form.web.trim() && form.email.trim() && form.telefono.trim();
    if (step === 2) return form.rol && form.objetivo && form.beneficios.length > 0 && (form.objetivo !== "otro" || form.objetivo_otro.trim());
    if (step === 3) {
      const modalidadOk = !cfg.showModalidad || form.modalidad;
      const clientesOk = !cfg.showClientes || form.clientes_potenciales;
      return modalidadOk && clientesOk && form.tiene_presupuesto && form.timeline && form.accepted_privacy;
    }
    return false;
  };

  const handleNext = () => {
    if (!canNext()) {
      toast.error("Completa todos los campos para continuar");
      return;
    }
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (!canNext()) {
      toast.error("Completa todos los campos y acepta la política de privacidad");
      return;
    }
    setIsSubmitting(true);
    try {
      const eventId = newEventId();
      const consentMarketing = hasMarketingConsent();

      await axios.post(`${API}/leads`, {
        ...form,
        origen: cfg.origen,
        event_id: eventId,
        consent_marketing: consentMarketing,
        page_url: window.location.href,
        fbp: readCookie("_fbp"),
        fbc: readCookie("_fbc"),
      });

      // Browser Pixel with the same event id → Meta deduplicates against CAPI
      trackLead(eventId);
      navigate("/propuesta-recibida");
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail || "Error al enviar. Inténtalo de nuevo.";
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToGuide = () => {
    document.getElementById("guia-cta")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="solicitar-propuesta"
      className="section-spacing bg-white relative overflow-hidden"
      data-testid="lead-form-section"
    >
      {/* Decorative orbs */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 mb-6">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700 font-body font-medium">{cfg.badge}</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            {cfg.title}
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            {cfg.subtitle}
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl shadow-blue-500/5 overflow-hidden">
          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100">
            <motion.div
              className="h-full bg-gradient-to-r from-galvani-blue to-galvani-purple"
              initial={{ width: "33.33%" }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          <div className="p-8 md:p-10 lg:p-12">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-8">
              <span className="text-xs uppercase tracking-widest text-galvani-textLight font-semibold">
                Paso {step} de 3
              </span>
              <div className="flex gap-2">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className={`w-8 h-1 rounded-full transition-colors ${
                      n <= step ? "bg-galvani-purple" : "bg-slate-200"
                    }`}
                  />
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-5"
                >
                  <h3 className="font-heading font-bold text-2xl text-galvani-text mb-2">
                    {cfg.step1Title}
                  </h3>
                  <p className="text-sm text-galvani-textLight mb-6">
                    {cfg.step1Subtitle}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-galvani-text text-sm mb-2 block">Nombre y apellido *</Label>
                      <Input
                        type="text"
                        placeholder="Tu nombre"
                        value={form.nombre}
                        onChange={(e) => update({ nombre: e.target.value })}
                        className="h-12 rounded-xl"
                        data-testid="lead-form-nombre"
                      />
                    </div>
                    <div>
                      <Label className="text-galvani-text text-sm mb-2 block">{cfg.empresaLabel}</Label>
                      <Input
                        type="text"
                        placeholder={cfg.empresaPlaceholder}
                        value={form.empresa}
                        onChange={(e) => update({ empresa: e.target.value })}
                        className="h-12 rounded-xl"
                        data-testid="lead-form-empresa"
                      />
                    </div>
                    <div>
                      <Label className="text-galvani-text text-sm mb-2 block">Email profesional *</Label>
                      <Input
                        type="email"
                        placeholder="tu@empresa.com"
                        value={form.email}
                        onChange={(e) => update({ email: e.target.value })}
                        className="h-12 rounded-xl"
                        data-testid="lead-form-email"
                      />
                    </div>
                    <div>
                      <Label className="text-galvani-text text-sm mb-2 block">Teléfono *</Label>
                      <Input
                        type="tel"
                        placeholder="+34 600 00 00 00"
                        value={form.telefono}
                        onChange={(e) => update({ telefono: e.target.value })}
                        className="h-12 rounded-xl"
                        data-testid="lead-form-telefono"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-galvani-text text-sm mb-2 block">{cfg.webLabel}</Label>
                      <Input
                        type="text"
                        placeholder={cfg.webPlaceholder}
                        value={form.web}
                        onChange={(e) => update({ web: e.target.value })}
                        className="h-12 rounded-xl"
                        data-testid="lead-form-web"
                      />
                      <p className="mt-2 text-xs text-galvani-textLight">
                        {cfg.webHint}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <h3 className="font-heading font-bold text-2xl text-galvani-text mb-2">
                    {cfg.step2Title}
                  </h3>
                  <p className="text-sm text-galvani-textLight mb-6">
                    {cfg.step2Subtitle}
                  </p>

                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">{cfg.rolLabel}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cfg.roles.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => update({ rol: r.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.rol === r.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-rol-${r.value}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">{cfg.objetivoLabel}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cfg.objetivos.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => update({ objetivo: o.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.objetivo === o.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-objetivo-${o.value}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {form.objetivo === "otro" && (
                      <div className="mt-4">
                        <Input
                          type="text"
                          placeholder="Cuéntanos brevemente cuál"
                          value={form.objetivo_otro}
                          onChange={(e) => update({ objetivo_otro: e.target.value })}
                          className="h-12 rounded-xl"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-galvani-text text-sm mb-1 block">{cfg.beneficiosLabel}</Label>
                    <p className="text-xs text-galvani-textLight mb-3">{cfg.beneficiosHint}</p>
                    <div className="grid grid-cols-1 gap-2.5">
                      {cfg.beneficios.map((b) => {
                        const selected = form.beneficios.includes(b.value);
                        return (
                          <button
                            key={b.value}
                            type="button"
                            onClick={() => toggleBeneficio(b.value)}
                            className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                              selected
                                ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                                : "border-slate-200 hover:border-slate-300 text-galvani-text"
                            }`}
                            data-testid={`lead-form-beneficio-${b.value}`}
                          >
                            <span
                              className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                selected
                                  ? "bg-galvani-purple border-galvani-purple"
                                  : "border-slate-300 bg-white"
                              }`}
                            >
                              {selected && <Check size={13} className="text-white" />}
                            </span>
                            {b.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <h3 className="font-heading font-bold text-2xl text-galvani-text mb-2">
                    {cfg.step3Title}
                  </h3>
                  <p className="text-sm text-galvani-textLight mb-6">
                    {cfg.step3Subtitle}
                  </p>

                  {cfg.showModalidad && (
                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">¿Dónde prefieres grabar? *</Label>
                    <div className="grid grid-cols-1 gap-3">
                      {MODALIDADES.map((m) => (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => update({ modalidad: m.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.modalidad === m.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-modalidad-${m.value}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}

                  {cfg.showClientes && (
                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">
                      ¿Cuántos clientes tuyos crees que encajarían con un videopodcast? *
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {CLIENTES_POTENCIALES.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => update({ clientes_potenciales: c.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.clientes_potenciales === c.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-clientes-${c.value}`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}

                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">{cfg.presupuestoLabel}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cfg.presupuestos.map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => update({ tiene_presupuesto: p.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.tiene_presupuesto === p.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-presupuesto-${p.value}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-galvani-text text-sm mb-3 block">{cfg.timingLabel}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {TIMINGS.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => update({ timeline: t.value })}
                          className={`p-4 rounded-xl border-2 text-left font-body font-medium text-sm transition-all ${
                            form.timeline === t.value
                              ? "border-galvani-purple bg-purple-50 text-galvani-purple"
                              : "border-slate-200 hover:border-slate-300 text-galvani-text"
                          }`}
                          data-testid={`lead-form-timing-${t.value}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${
                      form.accepted_privacy
                        ? "border-galvani-purple bg-purple-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      id="lead-privacy"
                      checked={form.accepted_privacy}
                      onCheckedChange={(checked) => update({ accepted_privacy: checked === true })}
                      className="mt-0.5 w-5 h-5 border-slate-400 data-[state=checked]:bg-galvani-purple data-[state=checked]:border-galvani-purple"
                      data-testid="lead-form-privacy"
                    />
                    <label htmlFor="lead-privacy" className="font-body text-sm text-galvani-text leading-relaxed cursor-pointer select-none">
                      He leído y acepto la{" "}
                      <a
                        href="/politica-privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-galvani-purple hover:underline font-semibold"
                      >
                        política de privacidad
                      </a>{" "}
                      {cfg.privacyText} *
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep((s) => s - 1)}
                  className="text-galvani-textLight hover:text-galvani-text font-body"
                  data-testid="lead-form-back"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Atrás
                </Button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 h-12 rounded-full shadow-lg shadow-blue-500/25"
                  data-testid="lead-form-next"
                >
                  Siguiente
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 h-12 rounded-full shadow-lg shadow-blue-500/25"
                  data-testid="lead-form-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Check size={16} className="mr-2" />
                      {cfg.submitLabel}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Secondary CTA — guía (oculto temporalmente) */}
        {false && (
          <div className="mt-10 text-center">
            <p className="text-sm text-galvani-textLight mb-3">
              ¿Aún explorando y prefieres empezar por informarte?
            </p>
            <button
              onClick={scrollToGuide}
              className="text-galvani-purple hover:text-galvani-blue font-body font-medium underline underline-offset-4 decoration-2 decoration-purple-200 hover:decoration-purple-500 transition-colors"
              data-testid="lead-form-scroll-to-guide"
            >
              Descarga primero la guía gratuita →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
